import React, { useEffect, useState } from "react";
import "@/App.css";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import ProtectedRoute from "@/components/common/ProtectedRoute";
import StatusBarSpace from "@/components/common/StatusBarSpace";
import NotFound from "@/pages/NotFound";
import FeedbackModal from "@/components/common/FeedbackModal";

import EnhancedConfirmDialog from "@/components/common/EnhancedConfirmDialog";
import "bootstrap/dist/css/bootstrap.min.css";
import "@/index.css";
import "@/reset.css";
import KakaoCallback from "@/pages/auth/KakaoCallback";
import Search from "@/pages/search/Search";
import Product from "@/pages/product/ProductPage";
import Login from "@/pages/login/LoginPage";
import NicknameInput from "@/pages/join/NicknameInput";
import UserInfoForm from "@/pages/join/UserInfoForm";
import Agreement from "@/pages/join/Agreement";
import JoinGuard from "@/components/common/JoinGuard";
import Home from "@/pages/home/HomePage";
import MyPage from "@/pages/user/MyPage";
import ReviewWritePage from "@/pages/review/ReviewWritePage";
import ReviewDetailPage from "@/pages/review/ReviewDetailPage";
import BottomNavigation from "@/components/BottomNavigation";
import SoolLogPage from "@/pages/soollog/SoolLogPage";
import ContentPage from "@/pages/content/ContentPage";
import logger from "@/utils/logger";

function App() {
    const location = useLocation();
    const [isTabExposure, setIsTabExposure] = useState(false);
    const [showFeedback, setShowFeedback] = useState(false);

    // 탭을 표시하지 않을 경로 목록
    const noTabPaths = [
        "/auth/kakao/callback", // 접두사 패턴
        "/join", // 접두사 패턴
        "/member", // 접두사 패턴
        "/alcohol/:altKey/review/new", // 특정 패턴
        "/mypage/review/:altKey" // 특정 패턴
    ];

    useEffect(() => {
        // 현재 경로가 탭을 표시하지 않을 경로 중 하나인지 확인
        const shouldHideTab = noTabPaths.some((path) => {
            // 접두사로 시작하는 경로 (예: /auth/*, /join/*, /member/*, /mypage/*)
            if (path.indexOf(":") === -1) {
                return location.pathname.startsWith(path);
            }
            // 동적 세그먼트가 있는 특정 경로 패턴 (예: /alcohol/:altKey/review/new)
            else {
                // 패턴과 실제 경로를 세그먼트로 분리
                const pathSegments = path.split("/");
                const locationSegments = location.pathname.split("/");

                // 세그먼트 수가 다르면 매치되지 않음
                if (pathSegments.length !== locationSegments.length) {
                    return false;
                }

                // 각 세그먼트 비교
                return pathSegments.every((segment, index) => {
                    // 동적 세그먼트(:로 시작)는 항상 매치
                    if (segment.startsWith(":")) {
                        return true;
                    }
                    return segment === locationSegments[index];
                });
            }
        });

        setIsTabExposure(!shouldHideTab);
    }, [location]);

    // 피드백 모달 표시 조건 상수
    const PRODUCT_VIEW_THRESHOLD = 2; // 피드백 모달을 표시하기 위한 상품 조회 횟수 임계값
    const FEEDBACK_STORAGE_KEY = "hasFeedbackShown";
    const PRODUCT_VIEW_COUNT_KEY = "productViewCount";

    // 피드백 모달 표시 로직
    useEffect(() => {
        // 로컬 스토리지에서 피드백 표시 여부 확인
        const hasFeedbackShown = localStorage.getItem(FEEDBACK_STORAGE_KEY) === "true";

        // 로그인, 회원가입 페이지에서는 피드백 모달을 표시하지 않음
        const isLoginOrJoinPage = location.pathname.startsWith("/auth/login") || location.pathname.startsWith("/join");

        if (!hasFeedbackShown && !isLoginOrJoinPage) {
            // 상품 상세 페이지 체크 및 조회 횟수 증가
            if (location.pathname.startsWith("/alcohol/") && !location.pathname.includes("/review")) {
                // 상품 조회 횟수 증가
                const currentCount = parseInt(localStorage.getItem(PRODUCT_VIEW_COUNT_KEY) || "0");
                const newCount = currentCount + 1;
                localStorage.setItem(PRODUCT_VIEW_COUNT_KEY, newCount.toString());

                logger.log(`상품 조회 횟수: ${newCount}`);

                // 임계값에 도달했다면 피드백 모달 표시
                if (newCount > PRODUCT_VIEW_THRESHOLD) {
                    setShowFeedback(true);
                }
            }
        }
    }, [location.pathname]);

    const handleFeedback = () => {
        // Tally 폼으로 이동
        window.open("https://tally.so/r/mVxQ6g", "_blank");
        setShowFeedback(false);
        localStorage.setItem(FEEDBACK_STORAGE_KEY, "true");
    };

    return (
        <EnhancedConfirmDialog>
            <div
                id="commonLayoutContainer"
                className="flex flex-col w-full m-auto min-w-[320px] max-w-[430px] min-h-screen bg-white relative safe-area-top"
            >
                {/* 메인 콘텐츠 영역 */}
                <div className="flex-1 w-full">
                    <Routes>
                        <Route path="/" element={<Navigate to="/home" replace />} />
                        <Route path="/home" element={<Home />} />
                        <Route path="/search" element={<Search />} />

                        {/* 술로그 페이지 */}
                        <Route
                            path="/soollog"
                            element={
                                <ProtectedRoute isAuthRoute={true} redirectPath="/auth/login">
                                    <SoolLogPage />
                                </ProtectedRoute>
                            }
                        />

                        {/* 콘텐츠 페이지 */}
                        <Route path="/content" element={<ContentPage />} />

                        {/* 인증 관련 라우트 (로그인 상태에서는 접근 불가) */}
                        <Route
                            path="/auth/login"
                            element={
                                <ProtectedRoute isAuthRoute={false} redirectPath="/home">
                                    <Login />
                                </ProtectedRoute>
                            }
                        />
                        {/* OAuth 콜백 라우트 */}
                        <Route path="/auth/kakao/callback" element={<KakaoCallback />} />

                        {/* 회원가입 플로우 라우트 */}
                        <Route
                            path="/join/agreement"
                            element={
                                <ProtectedRoute isAuthRoute={false} redirectPath="/home">
                                    <JoinGuard>
                                        <Agreement />
                                    </JoinGuard>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/join/nickname"
                            element={
                                <ProtectedRoute isAuthRoute={false} redirectPath="/home">
                                    <JoinGuard>
                                        <NicknameInput />
                                    </JoinGuard>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/join/userinfo"
                            element={
                                <ProtectedRoute isAuthRoute={false} redirectPath="/home">
                                    <JoinGuard>
                                        <UserInfoForm />
                                    </JoinGuard>
                                </ProtectedRoute>
                            }
                        />

                        {/* 상품 상세 페이지*/}
                        <Route path="/alcohol/:altKey" element={<Product />} />

                        {/* 리뷰 작성 페이지*/}
                        <Route
                            path="/alcohol/:altKey/review/new"
                            element={
                                <ProtectedRoute isAuthRoute={true} redirectPath="/home">
                                    <ReviewWritePage />
                                </ProtectedRoute>
                            }
                        />

                        {/* 마이페이지 라우트 */}
                        <Route
                            path="/mypage"
                            element={
                                <ProtectedRoute isAuthRoute={true} redirectPath="/auth/login">
                                    <MyPage />
                                </ProtectedRoute>
                            }
                        />

                        {/* 리뷰 상세 페이지 - 마이페이지에서 접근 */}
                        <Route
                            path="/mypage/review/:altKey"
                            element={
                                <ProtectedRoute isAuthRoute={true} redirectPath="/home">
                                    <ReviewDetailPage />
                                </ProtectedRoute>
                            }
                        />

                        {/* 404 페이지 - 모든 라우트의 마지막에 위치해야 함 */}
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </div>

                {/* 바텀 네비게이션 */}
                {isTabExposure && (
                    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white max-w-[430px] mx-auto">
                        <BottomNavigation />
                    </div>
                )}

                {showFeedback && (
                    <FeedbackModal
                        onClose={() => {
                            setShowFeedback(false);
                            localStorage.setItem(FEEDBACK_STORAGE_KEY, "true");
                        }}
                        onFeedback={handleFeedback}
                    />
                )}
            </div>
        </EnhancedConfirmDialog>
    );
}

export default App;
